<template>
  <div class="tagbox" :class="[$attrs.class, { 'tagbox--invalid': invalid }]" :style="$attrs.style">
    <!--
      ── The box ─────────────────────────────────────────────────────────────
      It looks and focuses like a text field on purpose: the value it holds
      used to BE a text field, and somebody who knew to type here should not
      have to learn a new control. What changed is that the commas are now
      drawn, so the list can be read at a glance and one item can be taken out
      without re-typing the four beside it.

      Clicking anywhere in the frame puts the cursor in the entry, the way
      clicking the padding of an <input> does.
    -->
    <div
      ref="frame"
      class="tagbox__frame"
      :class="{ 'tagbox__frame--dropping': dragging !== null }"
      @mousedown="onFrameMouseDown"
      @dragover.prevent
      @drop.prevent="dropAtEnd"
    >
      <span
        v-for="(item, index) in items"
        :key="`${item}-${index}`"
        class="tag"
        :class="{
          'tag--rejected': isRejected(item),
          'tag--unknown': isUnknown(item),
          'tag--dragging': dragging === index,
          'tag--over': dropTarget === index && dragging !== index,
          'tag--armed': armed === index,
        }"
        :draggable="!disabled"
        tabindex="0"
        :title="titleFor(item)"
        @keydown="onTagKeydown($event, index)"
        @focus="armed = index"
        @blur="armed = armed === index ? null : armed"
        @dragstart="onDragStart(index, $event)"
        @dragend="onDragEnd"
        @dragover.prevent="dropTarget = index"
        @drop.prevent="dropAt(index)"
      >
        <span class="tag__text">{{ labelOf(item) }}</span>
        <span v-if="unit" class="tag__unit">{{ unit }}</span>
        <button
          type="button"
          class="tag__remove"
          tabindex="-1"
          :aria-label="`${uiLabel('tagRemove')} ${labelOf(item)}`"
          :disabled="disabled"
          @click.stop="remove(index)"
        >×</button>
      </span>

      <!-- The entry. It keeps its own text until a separator or Enter turns it
           into a chip, so a half-typed word is never in the value. -->
      <!-- Las palabras de una lista no se corrigen ni se capitalizan solas: el
           corrector cambiaba «almacen» por otra cosa y el rol dejaba de casar.
           Van antes del v-bind para que quien llama pueda pedir otra cosa. -->
      <input
        ref="control"
        autocapitalize="off"
        autocorrect="off"
        :spellcheck="false"
        enterkeyhint="enter"
        v-bind="controlAttrs"
        :id="id"
        v-model="entry"
        class="tagbox__entry"
        type="text"
        :inputmode="numeric ? 'decimal' : undefined"
        :placeholder="items.length ? uiLabel('tagMore') : (placeholder ?? uiLabel('tagPlaceholder'))"
        :disabled="disabled || full"
        :aria-invalid="invalid || undefined"
        :aria-describedby="full ? hintId : undefined"
        autocomplete="off"
        @keydown="onEntryKeydown"
        @paste="onPaste"
        @focus="reveal()"
        @blur="commit(entry)"
      />
    </div>

    <!-- ── What else there is ─────────────────────────────────────────────
         Only ever offered, never enforced: a role can be created tomorrow and
         a phase can be called whatever the client calls it. Showing the ones
         not yet picked is the whole reason a list of role names stops being a
         guessing game. -->
    <!-- Cada propuesta lleva mousedown.prevent: tocarla le quitaba el foco a
         la entrada, el blur convertía lo medio escrito en una etiqueta («alm»
         además de «almacén») y se cerraba el teclado en una lista de varios. -->
    <div v-if="offered.length" ref="offer" class="offer">
      <span class="offer__label">{{ uiLabel('tagSuggestions') }}</span>
      <button
        v-for="option in offered"
        :key="option.value"
        type="button"
        class="offer__item"
        :disabled="disabled || full"
        @mousedown.prevent
        @click="pickOffer(option.value)"
      >
        <span aria-hidden="true">+</span>{{ option.label }}
      </button>
    </div>

    <p v-if="full" :id="hintId" class="tagbox__hint">{{ uiLabel('tagFull') }}</p>
  </div>
</template>

<script setup>
import { computed, nextTick, ref, useAttrs, useId, watch } from 'vue'

import { uiLabel } from '../utils/labels'
import { useRevealOnKeyboard } from '../composables/useSuggestionPopover'

/*
  ── A list, drawn ────────────────────────────────────────────────────────────

  Half the configuration of this ERP is a short list the client owns: the VAT
  rates it charges, the phases its projects go through, the roles an alert
  goes to. Every one of them was a text field holding `a, b, c`, which reads
  as a sentence rather than as a list — you cannot see how many there are, you
  cannot take the third one out without a careful double-click, and a stray
  comma or a trailing space is invisible until something downstream stops
  matching.

  So the commas are drawn. The value is still a list of strings and the caller
  still stores it however it likes; what this owns is the editing.

  Deliberately NOT a multi-select: these lists are open. `options` are
  suggestions — offered under the box so you can see what exists — and typing
  something that is not among them is allowed, because the role that will
  exist next week has to be typeable today. What the box does instead is MARK
  it, which is the part that was missing: a role name with a typo in it used
  to mean a notification nobody ever received, silently.
*/

defineOptions({ inheritAttrs: false })

const props = defineProps({
  /** The list itself. An array of strings — the caller owns how it is stored. */
  modelValue: { type: Array, default: () => [] },
  /**
   * What to offer under the box. Accepts ['a', 'b'] or [{ value, label }], the
   * same shape UiSelect takes, so a caller with translated names does not have
   * to invent a second convention.
   */
  options: { type: Array, default: () => [] },
  /** Shown muted inside every chip: `21 %`, `30 días`. */
  unit: { type: String, default: '' },
  /** Digits only, and a numeric keyboard on a phone. */
  numeric: Boolean,
  /**
   * The range a numeric item has to be in.
   *
   * Only ever used to MARK the chip that is out of it — the save is refused by
   * whoever owns the field, and by the server after that. What it buys is which
   * one: "Maximum 100" under a row of six rates is a sentence you have to go
   * hunting with.
   */
  min: { type: Number, default: undefined },
  max: { type: Number, default: undefined },
  /** Refuses more than this many items. 0 is no limit. */
  maxItems: { type: Number, default: 0 },
  placeholder: String,
  id: String,
  invalid: Boolean,
  disabled: Boolean,
})

const emit = defineEmits(['update:modelValue'])

const attrs = useAttrs()
const hintId = useId()

const control = ref(null)
const frame = ref(null)
const entry = ref('')
const offer = ref(null)

// Con el teclado fuera, el marco sube a lo alto de lo visible para que lo que
// se ofrece debajo no quede detrás de las teclas.
const { reveal } = useRevealOnKeyboard(frame, () => (offer.value?.offsetHeight ?? 0) + 8)

// Which chip a keyboard user is on, which one is being dragged, and which one
// the pointer is currently over. Three separate things: a chip can be focused
// while another is being dragged past it.
const armed = ref(null)
const dragging = ref(null)
const dropTarget = ref(null)

defineExpose({ focus: (options) => control.value?.focus(options) })

const controlAttrs = computed(() => Object.fromEntries(
  Object.entries(attrs).filter(([key]) => key !== 'class' && key !== 'style')
))

const items = computed(() => props.modelValue ?? [])
const full = computed(() => props.maxItems > 0 && items.value.length >= props.maxItems)

const normalised = computed(() =>
  props.options.map(option =>
    option !== null && typeof option === 'object'
      ? { value: String(option.value), label: option.label ?? String(option.value) }
      : { value: String(option), label: String(option) }
  )
)

const labelOf = (item) => normalised.value.find(option => option.value === item)?.label ?? item

/** Offered, in the order the caller declared them, minus what is already in. */
const offered = computed(() => normalised.value.filter(option => !items.value.includes(option.value)))

/**
 * A chip that matches nothing on offer.
 *
 * Only ever a warning. It is exactly true for the case it exists for — a role
 * name spelled `almacen` where the role is `almacén` — and merely premature
 * for the case where the option is about to be created, which is why it looks
 * like a note and not like an error.
 */
function isUnknown(item) {
  if (!normalised.value.length) return false
  return !normalised.value.some(option => option.value === item)
}

/** A numeric chip that is not a number, or is outside the range asked for. */
function isRejected(item) {
  if (!props.numeric) return false
  const value = Number(item)
  if (!Number.isFinite(value)) return true
  if (props.min !== undefined && value < props.min) return true
  if (props.max !== undefined && value > props.max) return true
  return false
}

function titleFor(item) {
  const notes = [uiLabel('tagReorder')]
  if (isUnknown(item)) notes.unshift(uiLabel('tagUnknown'))
  if (isRejected(item)) notes.unshift(uiLabel('tagRejected'))
  return notes.join(' · ')
}

// ── Changing the list ────────────────────────────────────────────────────────
function replace(next) {
  emit('update:modelValue', next)
}

/**
 * One item in.
 *
 * A duplicate is dropped rather than refused: the list already says what the
 * person was trying to say, and an error message about it would be pedantry.
 */
function add(raw) {
  const value = String(raw).trim()
  if (!value || full.value) return
  if (items.value.includes(value)) return
  replace([...items.value, value])
}

/**
 * Una propuesta tocada.
 *
 * Lo que había a medio escribir se borra si era el principio de esta misma
 * palabra: quien teclea «alm» y toca «almacén» estaba buscándola, no
 * escribiendo otra. Si no tiene que ver, se queda donde estaba.
 */
function pickOffer(value) {
  const typed = entry.value.trim().toLocaleLowerCase()
  const label = labelOf(value).toLocaleLowerCase()
  add(value)
  if (typed && (label.includes(typed) || String(value).toLocaleLowerCase().includes(typed))) entry.value = ''
}

/*
  El teclado de Android no dice qué tecla es: el keydown llega con la tecla
  «Unidentified», y la coma que en el escritorio cierra una etiqueta acababa
  escrita dentro de ella. Se mira lo escrito: lo que hay antes de un separador
  pasa a etiqueta, y lo de después se queda en la entrada.
*/
watch(entry, (text) => {
  if (!/[,;\n]/.test(text)) return
  const cut = Math.max(text.lastIndexOf(','), text.lastIndexOf(';'), text.lastIndexOf('\n'))
  const rest = text.slice(cut + 1)
  commit(text.slice(0, cut))
  entry.value = rest
})

function remove(index) {
  const next = [...items.value]
  next.splice(index, 1)
  replace(next)
  armed.value = null
  nextTick(() => control.value?.focus())
}

/**
 * Whatever is in the entry, turned into chips.
 *
 * Splits on commas and newlines, which is what makes the old value paste
 * straight in: somebody who copies `0, 4, 10, 21` out of the previous field —
 * or a column out of a spreadsheet — gets four chips and not one.
 */
function commit(text) {
  const pieces = String(text ?? '').split(/[,;\n\t]+/).map(piece => piece.trim()).filter(Boolean)
  entry.value = ''
  if (!pieces.length) return

  const next = [...items.value]
  for (const piece of pieces) {
    if (props.maxItems > 0 && next.length >= props.maxItems) break
    if (!next.includes(piece)) next.push(piece)
  }
  if (next.length !== items.value.length) replace(next)
}

function move(from, to) {
  if (to < 0 || to >= items.value.length || from === to) return
  const next = [...items.value]
  const [moved] = next.splice(from, 1)
  next.splice(to, 0, moved)
  replace(next)
  // Follow the chip: a reorder you cannot repeat without re-finding it is a
  // reorder you do once and give up on.
  nextTick(() => frame.value?.querySelectorAll('.tag')[to]?.focus())
}

// ── The entry's keys ─────────────────────────────────────────────────────────
function onEntryKeydown(event) {
  if (event.key === 'Enter' || event.key === ',' || event.key === ';') {
    event.preventDefault()
    commit(entry.value)
    return
  }

  // Backspace on an empty entry arms the last chip rather than deleting it, and
  // the next press takes it. Two keys for a destructive one-key habit: what
  // gets lost otherwise is the item you did not know was last.
  if (event.key === 'Backspace' && !entry.value && items.value.length) {
    event.preventDefault()
    frame.value?.querySelectorAll('.tag')[items.value.length - 1]?.focus()
    return
  }

  if (event.key === 'ArrowLeft' && !entry.value && items.value.length) {
    frame.value?.querySelectorAll('.tag')[items.value.length - 1]?.focus()
  }
}

function onPaste(event) {
  const text = event.clipboardData?.getData('text') ?? ''
  if (!/[,;\n\t]/.test(text)) return
  event.preventDefault()
  commit(`${entry.value}${text}`)
}

// ── A chip's keys ────────────────────────────────────────────────────────────
//
// Everything the mouse can do, without one: take an item out, and move it. The
// order is the client's answer in half these lists — the phases of a project,
// the severities worst first — so a control that can only be reordered by
// dragging is one an office keyboard cannot fix.
function onTagKeydown(event, index) {
  const chips = () => frame.value?.querySelectorAll('.tag') ?? []

  if (event.key === 'Backspace' || event.key === 'Delete') {
    event.preventDefault()
    remove(index)
    return
  }

  if (event.altKey && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
    event.preventDefault()
    move(index, index + (event.key === 'ArrowLeft' ? -1 : 1))
    return
  }

  if (event.key === 'ArrowLeft' && index > 0) {
    event.preventDefault()
    chips()[index - 1]?.focus()
    return
  }

  if (event.key === 'ArrowRight') {
    event.preventDefault()
    if (index + 1 < items.value.length) chips()[index + 1]?.focus()
    else control.value?.focus()
  }
}

// ── Dragging ─────────────────────────────────────────────────────────────────
function onDragStart(index, event) {
  dragging.value = index
  event.dataTransfer.effectAllowed = 'move'
  // Firefox refuses to start a drag with nothing on the transfer.
  event.dataTransfer.setData('text/plain', items.value[index] ?? '')
}

function onDragEnd() {
  dragging.value = null
  dropTarget.value = null
}

function dropAt(index) {
  if (dragging.value !== null) move(dragging.value, index)
  onDragEnd()
}

function dropAtEnd() {
  if (dragging.value !== null) move(dragging.value, items.value.length - 1)
  onDragEnd()
}

/**
 * Clicking the frame's own padding focuses the entry, as it would on an input.
 * On mousedown rather than click, and only when the target IS the frame, so it
 * does not steal the click that was landing on a chip's cross.
 */
function onFrameMouseDown(event) {
  if (event.target !== frame.value) return
  event.preventDefault()
  control.value?.focus()
}
</script>

<style scoped>
.tagbox { display: flex; flex-direction: column; gap: var(--space-2); min-width: 0; }

/*
  The frame is UiInput's, with the height let go: a list of nine phases wraps
  onto three lines and any fixed height would either clip it or leave a band of
  empty box under a list of two.
*/
.tagbox__frame {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  /* Chips start at the top of the box rather than floating in the middle of
     it. Nothing changes at the box's natural height — the padding leaves no
     slack there — and everything changes once a caller stretches it: the first
     chip then sits on the same line as the first chip of the box beside it. */
  align-content: flex-start;
  gap: var(--space-2);
  /* Grows into whatever room the caller gives it: a settings row hands two
     boxes the same height so a pair of lists reads as a pair. */
  flex: 1 1 auto;
  min-height: 38px;
  padding: 5px var(--space-3);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
  cursor: text;
  transition: border-color var(--fast), box-shadow var(--fast);
}
.tagbox__frame:focus-within {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px var(--color-primary-ring);
}
.tagbox__frame--dropping { border-style: dashed; }

.tagbox--invalid .tagbox__frame { border-color: var(--color-danger); }
.tagbox--invalid .tagbox__frame:focus-within { box-shadow: 0 0 0 3px var(--color-danger-soft); }

.tagbox__entry {
  flex: 1 1 90px;
  min-width: 90px;
  height: 26px;
  border: none;
  outline: none;
  background: transparent;
  color: inherit;
  font-size: var(--text-field);
}
.tagbox__entry::placeholder { color: var(--color-text-mute); }
.tagbox__entry:disabled { cursor: not-allowed; }

.tagbox__hint { font-size: var(--text-xs); color: var(--color-text-mute); }

/* ── A chip ──────────────────────────────────────────────────────────────── */
.tag {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  max-width: 100%;
  padding: 2px var(--space-1) 2px var(--space-2);
  border: 1px solid transparent;
  border-radius: var(--radius-sm);
  background: var(--color-primary-soft);
  color: var(--color-primary);
  font-size: var(--text-sm);
  font-weight: 600;
  line-height: 20px;
  white-space: nowrap;
  cursor: grab;
  transition: opacity var(--fast), box-shadow var(--fast), background var(--fast);
}
.tag:focus-visible { outline: none; box-shadow: 0 0 0 2px var(--color-primary); }
.tag:active { cursor: grabbing; }

.tag__text { overflow: hidden; text-overflow: ellipsis; }
.tag__unit { font-weight: 400; opacity: 0.7; }

/* Focused, and therefore one Backspace from gone. Says so. */
.tag--armed { box-shadow: 0 0 0 2px var(--color-primary); }

/* Something typed that matches nothing on offer. A note, not an error: it is
   usually a typo and occasionally a role that does not exist yet. */
.tag--unknown {
  background: var(--color-warning-soft);
  color: var(--color-warning);
  border-color: color-mix(in srgb, var(--color-warning) 35%, transparent);
}

/* Not a number, or outside the range. Red rather than amber, because unlike an
   unknown role this one WILL be refused — the row already says why, and this
   says which. */
.tag--rejected {
  background: var(--color-danger-soft);
  color: var(--color-danger);
  border-color: color-mix(in srgb, var(--color-danger) 45%, transparent);
}

.tag--dragging { opacity: 0.4; }
.tag--over { box-shadow: -3px 0 0 0 var(--color-primary); }

.tag__remove {
  display: grid;
  place-items: center;
  width: 18px; height: 18px;
  flex-shrink: 0;
  padding: 0;
  border: none;
  border-radius: var(--radius-sm);
  background: none;
  color: inherit;
  font-size: 15px;
  line-height: 1;
  opacity: 0.55;
  transition: opacity var(--fast), background var(--fast), transform var(--fast);
}
.tag__remove:hover:not(:disabled) {
  opacity: 1;
  background: color-mix(in srgb, currentColor 18%, transparent);
  transform: rotate(90deg);
}
.tag__remove:disabled { display: none; }

/* ── What else there is ──────────────────────────────────────────────────── */
.offer { display: flex; flex-wrap: wrap; align-items: center; gap: var(--space-2); }

.offer__label {
  font-size: var(--text-xs);
  color: var(--color-text-mute);
}

.offer__item {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 1px var(--space-2);
  border: 1px dashed var(--color-border);
  border-radius: var(--radius-sm);
  background: none;
  color: var(--color-text-mute);
  font-size: var(--text-xs);
  line-height: 18px;
  white-space: nowrap;
  transition: color var(--fast), border-color var(--fast), background var(--fast);
}
.offer__item:hover:not(:disabled) {
  border-style: solid;
  border-color: var(--color-primary);
  background: var(--color-primary-soft);
  color: var(--color-primary);
}
.offer__item:disabled { opacity: 0.5; }

/* Igual que cualquier otro campo: más alto bajo un pulgar, y la cruz de cada
   etiqueta lo bastante grande para acertar sin ampliar.

   28 y no 44, a diferencia del resto del ui-kit, y es la única excepción que se
   hace: una etiqueta es EN LÍNEA y caben varias en un renglón. A 44 px cada una,
   tres etiquetas ocupan la pantalla de un móvil y el campo deja de leerse. 24
   era lo que había y es el mínimo exacto de la norma, que no es holgura: es el
   suelo. La etiqueta crece con la cruz para que no se salga de ella. */
@media (pointer: coarse) {
  .tagbox__frame { min-height: 44px; }
  .tag { line-height: 28px; }
  .tag__remove { width: 28px; height: 28px; }
  /* Las propuestas sí son lo que se toca para elegir, como las filas de una
     lista de sugerencias: 44 px de alto, y caben varias por renglón igual. */
  .offer__item { min-height: 44px; padding: 0 var(--space-3); font-size: var(--text-sm); }
}
</style>
